import type { StrategySettings, DraftRate, WageRate, ScienceRate, ScienceBranch } from '../types/strategy';

export interface PremadeTemplate {
  key: string;
  name: string;
  description: string;
  settings: StrategySettings;
}

const ALL_BUILDINGS: Record<string, number> = {
  HOME: 0, FARM: 0, MILL: 0, BANK: 0, TRAINING_GROUND: 0, ARMOURY: 0,
  MILITARY_BARRACK: 0, FORT: 0, GUARD_STATION: 0, HOSPITAL: 0, GUILD: 0,
  TOWER: 0, THIEVES_DEN: 0, WATCH_TOWER: 0, LIBRARY: 0, SCHOOL: 0,
  STABLE: 0, DUNGEON: 0,
};

function makeSettings(overrides: {
  buildings: Partial<Record<string, number>>;
  draftLevel: number;
  draftRate: DraftRate;
  wageRate: WageRate;
  scienceRate: ScienceRate;
  scienceAlloc: Record<ScienceBranch, number>;
  armyComp: { offSpec: number; defSpec: number; elite: number };
  thiefPA: number;
  wizardPA: number;
}): StrategySettings {
  return {
    buildings: { ...ALL_BUILDINGS, ...overrides.buildings } as Record<string, number>,
    draftLevel: overrides.draftLevel,
    draftRate: overrides.draftRate,
    wageRate: overrides.wageRate,
    scienceRate: overrides.scienceRate,
    scienceAlloc: overrides.scienceAlloc,
    armyComp: overrides.armyComp,
    thiefPA: overrides.thiefPA,
    wizardPA: overrides.wizardPA,
  };
}

export const PREMADE_TEMPLATES: PremadeTemplate[] = [
  {
    key: 'Attacker',
    name: 'Attacker',
    description: 'High offense, strong elites',
    settings: makeSettings({
      buildings: {
        HOME: 4, FARM: 20, MILL: 2, BANK: 8, TRAINING_GROUND: 12, ARMOURY: 8,
        FORT: 4, LIBRARY: 4, SCHOOL: 4, STABLE: 10, DUNGEON: 4,
      },
      draftLevel: 70, draftRate: 'AGGRESSIVE', wageRate: 100, scienceRate: 'SUSTAINED',
      scienceAlloc: { alchemy: 15, tools: 10, housing: 10, food: 10, military: 35, thievery: 10, channeling: 10 },
      armyComp: { offSpec: 3.0, defSpec: 0.5, elite: 5.0 },
      thiefPA: 0, wizardPA: 0,
    }),
  },
  {
    key: 'Mystic',
    name: 'Mystic',
    description: 'Magic-focused, high WPA',
    settings: makeSettings({
      buildings: {
        HOME: 6, FARM: 22, MILL: 2, BANK: 10, TRAINING_GROUND: 2, ARMOURY: 4,
        FORT: 6, GUILD: 18, TOWER: 14, LIBRARY: 4, SCHOOL: 4,
      },
      draftLevel: 40, draftRate: 'NORMAL', wageRate: 100, scienceRate: 'FOCUSED',
      scienceAlloc: { alchemy: 10, tools: 5, housing: 10, food: 10, military: 10, thievery: 5, channeling: 50 },
      armyComp: { offSpec: 0.5, defSpec: 1.0, elite: 2.0 },
      thiefPA: 0, wizardPA: 3.0,
    }),
  },
  {
    key: 'Thief',
    name: 'Thief',
    description: 'Covert ops, high TPA',
    settings: makeSettings({
      buildings: {
        HOME: 6, FARM: 22, MILL: 2, BANK: 10, TRAINING_GROUND: 2, ARMOURY: 4,
        FORT: 8, THIEVES_DEN: 16, LIBRARY: 4, SCHOOL: 4, DUNGEON: 6,
      },
      draftLevel: 45, draftRate: 'NORMAL', wageRate: 100, scienceRate: 'ACTIVE',
      scienceAlloc: { alchemy: 10, tools: 5, housing: 10, food: 10, military: 10, thievery: 50, channeling: 5 },
      armyComp: { offSpec: 0.5, defSpec: 1.0, elite: 2.0 },
      thiefPA: 3.0, wizardPA: 0,
    }),
  },
  {
    key: 'Science Push',
    name: 'Science Push',
    description: 'Heavy knowledge investment',
    settings: makeSettings({
      buildings: {
        HOME: 6, FARM: 24, MILL: 2, BANK: 14, TRAINING_GROUND: 2, ARMOURY: 4,
        FORT: 6, LIBRARY: 12, SCHOOL: 14,
      },
      draftLevel: 35, draftRate: 'RESERVIST', wageRate: 100, scienceRate: 'ACCELERATED',
      scienceAlloc: { alchemy: 20, tools: 15, housing: 15, food: 15, military: 15, thievery: 10, channeling: 10 },
      armyComp: { offSpec: 0.5, defSpec: 1.0, elite: 1.5 },
      thiefPA: 0.5, wizardPA: 0,
    }),
  },
];

export const DEFAULT_STRATEGY_SETTINGS: StrategySettings = {
  buildings: { ...ALL_BUILDINGS },
  draftLevel: 50,
  draftRate: 'NORMAL',
  wageRate: 100,
  scienceRate: 'SUSTAINED',
  scienceAlloc: { alchemy: 15, tools: 15, housing: 15, food: 15, military: 15, thievery: 15, channeling: 10 },
  armyComp: { offSpec: 1.0, defSpec: 1.0, elite: 2.0 },
  thiefPA: 0,
  wizardPA: 0,
};
